import { zSetting } from 'app/frontend/base/helpers/forms'
import { z } from 'zod'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useDiscordReferences } from 'app/frontend/base/config/queries'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { imageUrl } from 'app/frontend/base/helpers/hooks'
import { infoByType } from 'app/frontend/base/pages/UserDashboard/subpages/UserLinkedAccounts/components/LinkedAccount'
import { useState } from 'react'
import DiscordBotInstructionModal from '../../../../components/DiscordBotInstructionModal'
import { CircleQuestionMarkIcon } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'

const formSchema = z.object({
  settings: z.object({
    raidsDiscordPostTimezone: zSetting(z.string()),
    raidsDeleteDiscordPostAfter: zSetting(z.number().or(z.string())),
  }),
})

const DiscordBotSection = () => {
  const [viewingInstructions, setViewingInstructions] = useState(false)
  const { team } = useTeam()
  const { data: discordReferences, isPending } = useDiscordReferences(team?.id)

  return (
    <FormWrapper<wowaudit.Team>
      key={team?.id}
      formSchema={formSchema}
      entityType='team'
      entity={team}
    >
      <DiscordBotInstructionModal
        setOpen={setViewingInstructions}
        open={viewingInstructions}
      />

      {isPending ? null : (
        <div>
          {discordReferences?.length === 0 ? (
            <div className='mb-4'>
              <Alert variant='info'>
                <AlertTitle className='flex items-center gap-3'>
                  <img
                    src={imageUrl(infoByType.Discord.logo)}
                    className='w-6'
                  />
                  Discord bot not installed
                  <CircleQuestionMarkIcon
                    className='w-4 h-4 cursor-pointer'
                    onClick={() => setViewingInstructions(true)}
                  />
                </AlertTitle>
                <AlertDescription className='flex items-center gap-2'>
                  In order to use Discord features (including notifications),
                  you need to have the bot installed in your server.
                  <a
                    href='/get_discord_bot'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Button
                      type='button'
                      size='sm'
                      onClick={() => setViewingInstructions(true)}
                    >
                      Add to server
                    </Button>
                  </a>
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className='mb-8'>
              <h4 className='flex items-center justify-between gap-4'>
                <span className='flex items-center gap-2'>
                  <span>Active servers</span>
                  <CircleQuestionMarkIcon
                    className='w-4 h-4 cursor-pointer'
                    onClick={() => setViewingInstructions(true)}
                  />
                </span>
                <a
                  href='/get_discord_bot'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Button type='button' size='xs'>
                    Install bot
                  </Button>
                </a>
              </h4>
              <p className='mt-6 mb-4'>
                The Discord bot is installed and configured for this team in the
                following servers.
              </p>
              {discordReferences?.map((reference) => (
                <Card
                  key={reference.serverId}
                  className='flex items-center gap-3 mb-3 p-3 justify-between'
                >
                  <div className='flex items-center gap-3 text-base'>
                    <img
                      className={`rounded-md w-8 h-8`}
                      src={
                        reference.serverIconUrl ?? imageUrl('question-temp.png')
                      }
                    />
                    <a
                      href={`https://discord.com/channels/${reference.serverId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {reference.serverName ?? (
                        <span className='italic opacity-70'>
                          Anonymous server
                        </span>
                      )}
                    </a>
                    {reference.channelId && (
                      <a
                        href={`https://discord.com/channels/${reference.serverId}/${reference.channelId}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-muted-foreground'
                      >
                        {reference.channelName
                          ? `#${reference.channelName}`
                          : `(anonymous channel)`}
                      </a>
                    )}
                  </div>

                  <div className='text-sm text-muted-foreground'>
                    since {reference.createdAt}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <SettingField name='raidsDiscordPostTimezone' />
      <SettingField name='raidsDeleteDiscordPostAfter' layout='box' />
    </FormWrapper>
  )
}

export default DiscordBotSection
